import { ConfigurableFocusTrapFactory } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Inject,
	Input,
	OnInit,
	Optional,
	Output,
	ViewContainerRef,
} from "@angular/core";
import { UntypedFormControl, Validators } from "@angular/forms";
import { resolveClientDisplayColor } from "@app/_helpers/client-color";
import { fromRxValue } from "@app/_helpers/utils";
import { DefaultEntityTranslatePipe } from "@app/_pipes/default-entity-translate.pipe";
import { AppService } from "@app/app.service";
import { SatPopoverComponent as SatPopover, SatPopoverAnchoringService } from "@ncstate/sat-popover";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Client, ClientsQuery, UserSettingsQuery } from "@timeghost/web-api";
import { xor } from "lodash";
import { BehaviorSubject, combineLatest } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { firstBy } from "thenby";

@UntilDestroy()
@Component({
	selector: "tg-client-filter-popover",
	templateUrl: "./client-filter-popover.component.html",
	styleUrls: ["./client-filter-popover.component.scss"],
	providers: [{ provide: SatPopoverAnchoringService, useExisting: false }],
})
export class ClientFilterPopoverComponent extends SatPopover implements OnInit {
	constructor(
		private userSettingsQuery: UserSettingsQuery,
		private clientsQuery: ClientsQuery,
		private appService: AppService,
		private translateEntity: DefaultEntityTranslatePipe,
		_focusTrapFactory: ConfigurableFocusTrapFactory,
		_anchoringService: SatPopoverAnchoringService,
		_viewContainerRef: ViewContainerRef,
		@Optional() @Inject(DOCUMENT) _document: any,
		private cdr: ChangeDetectorRef
	) {
		super(
			_focusTrapFactory,
			_anchoringService,
			_viewContainerRef,
			"200ms cubic-bezier(0.25, 0.8, 0.25, 1)",
			_document
		);
	}

	search = new UntypedFormControl("", [Validators.required, Validators.minLength(2)]);
	private _selectedItems = new BehaviorSubject<string[]>([]);
	readonly selectedItems$ = this._selectedItems.asObservable().pipe(distinctUntilChanged());
	filteredClients: Client[] = [];

	get selectedItems() {
		return this._selectedItems.getValue();
	}

	@Input()
	set selectedItems(val: string[]) {
		const prev = this.selectedItems;
		this._selectedItems.next(val);

		if (xor(val, prev).length > 0) this.selectedItemsChange.emit(val);
	}

	@Output() selectedItemsChange = new EventEmitter(true);

	items$filtered = fromRxValue(
		combineLatest([
			this.search.valueChanges.pipe(startWith(this.search.value), distinctUntilChanged()),
			this.clientsQuery.selectAll(),
			this.selectedItems$,
		]).pipe(
			map(([q, clients, selected]: [string, Client[], string[]]) => {
				const newClients = (() => {
					if (this.search.invalid) return clients;
					return clients.filter((x) => {
						return (
							this.translateEntity
								.transform(x, "client", "client.none")
								?.toLowerCase()
								.indexOf(q.toLowerCase()) !== -1
						);
					});
				})()
					.map((x) => {
						return {
							...x,
							selected: selected.findIndex((u) => u === x.id) !== -1,
						};
					})
					.sort(
						firstBy("selected", "desc")
							// @ts-ignore
							.thenBy((x) => x.useAsDefault)
							.thenBy("name", "asc")
					);
				this.filteredClients = newClients;
				return newClients;
			})
		)
	);

	@Output() itemsClose = new EventEmitter(true);

	ngOnInit(): void {
		this.openAnimationStartAtScale = this.closeAnimationEndAtScale = 0.9375;
		this.hasBackdrop = true;
		this.yAlign = "below";
		this.xAlign = "center";
		this.closed.asObservable().subscribe(() => this.search.reset(""));
		this.closed
			.asObservable()
			.pipe(
				untilDestroyed(this),
				map(() => this.selectedItems),
				distinctUntilChanged()
			)
			.subscribe((val) => this.itemsClose.emit(val));
	}

	trackId(index: number, { id }: { id: string }) {
		return id;
	}

	clientDisplayColor(client: Client & { color?: string | null }) {
		return resolveClientDisplayColor(client.color, client.id, (n) =>
			this.appService.colorScheme.getColorFromScheme(n)
		);
	}

	selectItem(id: string) {
		if (this.selectedItems.findIndex((x) => x === id) !== -1)
			this.selectedItems = this.selectedItems.filter((x) => x !== id);
		else this.selectedItems = [...this.selectedItems, id];
	}

	selectAll() {
		this.selectedItems = this.items$filtered.value.map((client) => client.id);
	}

	selectNone() {
		this.selectedItems = [];
	}

	close() {
		super.close(this.selectedItems);
	}
}
